import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LogOut, Settings as SettingsIcon, Bell, ArrowLeft, BookOpen, Lock, Rocket,
} from 'lucide-react';
import { PLATFORM_NAV_ITEMS } from '../config/platformNav';
import useEntitlements, { NAV_MODULE_MAP, OPEN_NAV_IDS } from '../hooks/useEntitlements';
import { useUiTheme } from '../context/UiThemeContext';
import { useWhitelabelBrand } from '../context/WhitelabelBrandContext';
import '../styles/dashboard-brand.css';

/**
 * DashboardShell — shared sidebar/header chrome used by Dashboard, Settings,
 * and other authenticated pages so the platform UI feels uniform.
 * Brand: yellow · black · white (matches landing).
 */
const NAV_ITEMS = PLATFORM_NAV_ITEMS;

const chipBtn = 'inline-flex items-center gap-1.5 rounded-full border-2 border-[#16130d] bg-[#fbbf0f] px-3 py-1.5 text-xs font-bold text-[#16130d] shadow-[0_3px_0_0_#16130d] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_#16130d] transition';
const ghostChip = 'inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/[0.06] px-3 py-1.5 text-xs font-semibold text-[#f5f0e6] hover:bg-[#fbbf0f]/15 hover:border-[#fbbf0f]/50 hover:text-white transition';

export default function DashboardShell({
  user,
  title = '',
  subtitle = '',
  activeNav = '',
  children,
  headerExtras = null,
  brandName: brandNameProp,
  logoUrl: logoUrlProp,
  fullscreen = false,
  backPath = '/dashboard',
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { canAny, entitlements } = useEntitlements();
  const { theme } = useUiTheme();
  const { brandName: ctxBrand, logoUrl: ctxLogo } = useWhitelabelBrand();
  const brandName = (brandNameProp != null && String(brandNameProp).trim())
    ? String(brandNameProp).trim()
    : (ctxBrand || 'Snagcart');
  const logoUrl = logoUrlProp !== undefined ? logoUrlProp : ctxLogo;
  const [logoBroken, setLogoBroken] = React.useState(false);
  React.useEffect(() => { setLogoBroken(false); }, [logoUrl]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  const navAllowed = (item) => {
    if (OPEN_NAV_IDS.has(item.id)) return true;
    if (!entitlements) return true;
    const mods = NAV_MODULE_MAP[item.id];
    if (!mods) return true;
    return canAny(mods);
  };

  const BrandMark = ({ name }) => (
    <div className="flex items-center gap-2.5 min-w-0" data-testid="shell-brand">
      <div
        className="w-8 h-8 shrink-0 rounded-xl flex items-center justify-center border-2 border-[#16130d] shadow-[0_3px_0_0_#16130d]"
        style={{ background: '#fbbf0f' }}
      >
        {logoUrl && !logoBroken ? (
          <img
            src={logoUrl}
            alt="Logo"
            className="w-full h-full rounded-lg object-cover"
            data-testid="shell-logo-img"
            onError={() => setLogoBroken(true)}
          />
        ) : (
          <Rocket className="w-4 h-4 text-[#16130d] -rotate-45" strokeWidth={2.4} data-testid="shell-logo-rocket" />
        )}
      </div>
      <span
        className="truncate text-sm font-sans font-semibold tracking-normal text-white"
        style={{ fontFamily: "'IBM Plex Sans', Manrope, sans-serif" }}
        data-testid="shell-logo"
      >
        {name || brandName}
      </span>
    </div>
  );

  if (fullscreen) {
    const moduleName = (title && String(title).trim()) || brandName;
    const backLabel = backPath === '/dashboard' || backPath === '/'
      ? 'Dashboard'
      : backPath.includes('ai-cofounder')
        ? 'AI Co-founder'
        : 'Back';
    const goBack = () => {
      if (backPath === '/ai-cofounder') {
        navigate(backPath, { state: { resumeWorkspace: true } });
        return;
      }
      navigate(backPath);
    };
    return (
      <div
        className="dash-brand flex h-[100dvh] w-full flex-col overflow-hidden text-[#faf7f2]"
        data-testid="dashboard-shell-fullscreen"
        data-ui-theme={theme}
      >
        <header className="z-40 flex h-14 shrink-0 items-center gap-3 border-b border-[#fbbf0f]/20 bg-[#0a0908]/95 px-3 backdrop-blur-xl sm:px-5">
          <button
            type="button"
            onClick={goBack}
            className={ghostChip}
            data-testid="shell-fullscreen-back"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{backLabel}</span>
          </button>
          <div className="min-w-0 shrink">
            <BrandMark name={moduleName} />
          </div>
          <div className="min-w-0 flex-1">
            {subtitle ? (
              <p className="truncate text-[11px] text-[#c4b8a5]" data-testid="shell-subtitle">{subtitle}</p>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {headerExtras}
            <button
              type="button"
              onClick={() => navigate('/platform-guide')}
              className="flex h-8 w-8 items-center justify-center rounded-full border border-white/20 bg-white/[0.06] text-[#fbbf0f] hover:bg-[#fbbf0f]/15"
              title="Platform Guide"
              data-testid="shell-guide-btn"
            >
              <BookOpen className="h-3.5 w-3.5" />
            </button>
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </div>
    );
  }

  return (
    <div
      className="dash-brand min-h-screen flex text-[#faf7f2]"
      data-testid="dashboard-shell"
      data-ui-theme={theme}
    >
      <aside className="fixed left-0 top-0 h-full w-60 z-50 hidden md:flex flex-col border-r border-[#fbbf0f]/15 bg-[#11100c]">
        <div className="px-5 py-5 border-b border-[#fbbf0f]/15">
          <BrandMark />
        </div>

        <nav className="flex-1 px-3 py-4 overflow-y-auto space-y-0.5">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const allowed = navAllowed(item);
            const isActive = item.id === activeNav
              || location.pathname === item.path
              || (item.path !== '/dashboard' && location.pathname.startsWith(`${item.path}/`));
            return (
              <button
                key={item.id}
                onClick={() => navigate(allowed ? item.path : '/subscription-plans')}
                data-testid={`shell-nav-${item.id}`}
                title={allowed ? item.label : 'Upgrade plan to unlock'}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  !allowed
                    ? 'text-[#6b6256] hover:text-[#a89882] hover:bg-white/[0.03]'
                    : isActive
                      ? 'bg-[#fbbf0f] text-[#16130d] border-2 border-[#16130d] shadow-[0_3px_0_0_#16130d]'
                      : 'text-[#d9cebc] hover:text-white hover:bg-white/[0.05] border-2 border-transparent'
                }`}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 ${allowed && isActive ? 'text-[#16130d]' : allowed ? 'text-[#fbbf0f]' : ''}`} />
                <span className="truncate flex-1 text-left">{item.label}</span>
                {!allowed ? <Lock className="w-3 h-3 flex-shrink-0 text-[#6b6256]" /> : null}
              </button>
            );
          })}
        </nav>

        <div className="px-3 py-4 border-t border-[#fbbf0f]/15 space-y-0.5">
          <button
            onClick={() => navigate('/settings')}
            data-testid="shell-nav-settings"
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeNav === 'settings'
                ? 'bg-[#fbbf0f] text-[#16130d] border-2 border-[#16130d] shadow-[0_3px_0_0_#16130d]'
                : 'text-[#d9cebc] hover:text-white hover:bg-white/[0.05] border-2 border-transparent'
            }`}
          >
            <SettingsIcon className={`w-4 h-4 ${activeNav === 'settings' ? 'text-[#16130d]' : 'text-[#fbbf0f]'}`} />
            <span>Settings</span>
          </button>
          <div className="px-3 py-2 mt-2">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-[#fbbf0f] border-2 border-[#16130d] text-[#16130d] flex items-center justify-center text-xs font-extrabold flex-shrink-0 shadow-[0_2px_0_0_#16130d]">
                {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold truncate text-white" data-testid="shell-user-name">{user?.name || user?.email || 'User'}</p>
                <p className="text-[10px] truncate text-[#a89882]">{user?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                data-testid="shell-logout"
                className="text-[#a89882] hover:text-[#fbbf0f] transition-colors"
                title="Logout"
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      <main className="flex-1 md:ml-60 min-h-screen">
        {(title || headerExtras) && (
          <header className="sticky top-0 z-40 backdrop-blur-xl border-b border-[#fbbf0f]/15 bg-[#0a0908]/90 px-6 py-3.5 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              {title && (
                <h1
                  className="text-sm font-sans font-semibold tracking-normal text-white"
                  style={{ fontFamily: "'IBM Plex Sans', Manrope, sans-serif" }}
                  data-testid="shell-title"
                >
                  {title}
                </h1>
              )}
              {subtitle && (
                <p className="text-xs text-[#c4b8a5] mt-0.5" data-testid="shell-subtitle">{subtitle}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {headerExtras}
              <button
                type="button"
                onClick={() => navigate('/platform-guide')}
                className="w-9 h-9 rounded-full border border-white/20 bg-white/[0.06] flex items-center justify-center text-[#fbbf0f] hover:bg-[#fbbf0f]/15 transition-all"
                title="Platform Guide"
                data-testid="shell-guide-btn"
              >
                <BookOpen className="w-3.5 h-3.5" />
              </button>
              <button
                className="w-9 h-9 rounded-full border border-white/20 bg-white/[0.06] flex items-center justify-center text-[#d9cebc] hover:text-[#fbbf0f] hover:bg-[#fbbf0f]/15 transition-all"
                title="Notifications"
              >
                <Bell className="h-3.5 w-3.5" />
              </button>
            </div>
          </header>
        )}
        {children}
      </main>
    </div>
  );
}

// Export chip styles for pages that want matching header buttons
export { chipBtn, ghostChip };
